import * as React from "react"
import {
  getStudentAttendanceByDate,
  getStudentAttendanceSummary,
} from "../auth/api"

const WEEKDAYS = ["D", "S", "T", "Q", "Q", "S", "S"]

function pad(n) {
  return String(n).padStart(2, "0")
}

function toMonthString(date) {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}`
}

function toDateString(date) {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
}

export default function StudentAttendanceSection() {
  const today = React.useMemo(() => new Date(), [])
  const [monthCursor, setMonthCursor] = React.useState(
    new Date(today.getFullYear(), today.getMonth(), 1)
  )
  const [selectedDate, setSelectedDate] = React.useState(toDateString(today))
  const [summary, setSummary] = React.useState({})
  const [dayDetail, setDayDetail] = React.useState(null)
  const [loadingSummary, setLoadingSummary] = React.useState(true)
  const [loadingDetail, setLoadingDetail] = React.useState(true)
  const [error, setError] = React.useState("")
  const [showMonthPicker, setShowMonthPicker] = React.useState(false)

  React.useEffect(() => {
    let mounted = true
    setLoadingSummary(true)
    getStudentAttendanceSummary(toMonthString(monthCursor))
      .then((list) => {
        if (!mounted) return
        const map = {}
        list.forEach((item) => {
          map[item.data] = item.horario
        })
        setSummary(map)
      })
      .catch((err) => {
        if (mounted) setError(err?.message || "Erro ao carregar calendário.")
      })
      .finally(() => {
        if (mounted) setLoadingSummary(false)
      })
    return () => {
      mounted = false
    }
  }, [monthCursor])

  React.useEffect(() => {
    let mounted = true
    setLoadingDetail(true)
    getStudentAttendanceByDate(selectedDate)
      .then((detail) => {
        if (mounted) setDayDetail(detail)
      })
      .catch((err) => {
        if (mounted) setError(err?.message || "Erro ao carregar presença do dia.")
      })
      .finally(() => {
        if (mounted) setLoadingDetail(false)
      })
    return () => {
      mounted = false
    }
  }, [selectedDate])

  const daysGrid = React.useMemo(() => {
    const year = monthCursor.getFullYear()
    const month = monthCursor.getMonth()
    const firstWeekday = new Date(year, month, 1).getDay()
    const totalDays = new Date(year, month + 1, 0).getDate()

    const cells = []
    for (let i = 0; i < firstWeekday; i++) cells.push(null)
    for (let day = 1; day <= totalDays; day++) {
      cells.push(new Date(year, month, day))
    }
    return cells
  }, [monthCursor])

  function changeMonth(delta) {
    setMonthCursor((prev) => new Date(prev.getFullYear(), prev.getMonth() + delta, 1))
  }

  function jumpToMonth(value) {
    const [year, month] = value.split("-").map(Number)
    if (year && month) {
      setMonthCursor(new Date(year, month - 1, 1))
    }
    setShowMonthPicker(false)
  }

  const monthLabel = monthCursor.toLocaleDateString("pt-BR", {
    month: "long",
    year: "numeric",
  })

  const selectedDateLabel = new Date(`${selectedDate}T00:00:00`).toLocaleDateString(
    "pt-BR",
    { day: "2-digit", month: "long", year: "numeric" }
  )

  const presentDaysCount = Object.keys(summary).length

  return (
    <div className="bg-slate-50 backdrop-blur rounded-2xl p-4 sm:p-6 shadow-2xl">
      <div>
        <h2 className="text-2xl sm:text-3xl font-semibold">Minhas presenças</h2>
        <p className="text-slate-950 mt-1">
          Veja os dias em que você compareceu às aulas.
        </p>
      </div>

      {error && <div className="text-red-700 mt-4">{error}</div>}

      <div className="mt-6 flex items-center justify-between relative">
        <button
          onClick={() => changeMonth(-1)}
          type="button"
          aria-label="Mês anterior"
          className="px-3 py-1.5 rounded-lg border-2 border-cor-base text-cor-base font-semibold hover:scale-[1.05] transition-all"
        >
          ←
        </button>

        <div className="relative">
          <button
            type="button"
            onClick={() => setShowMonthPicker((prev) => !prev)}
            className="font-semibold text-slate-900 capitalize px-3 py-1.5 rounded-lg hover:bg-slate-200 transition-all"
          >
            {monthLabel}
          </button>

          {showMonthPicker && (
            <>
              <div
                className="fixed inset-0 z-10"
                onClick={() => setShowMonthPicker(false)}
              />
              <div className="absolute z-20 top-full mt-2 left-1/2 -translate-x-1/2 bg-white border-2 border-slate-200 rounded-xl p-3 shadow-2xl">
                <input
                  type="month"
                  autoFocus
                  defaultValue={toMonthString(monthCursor)}
                  onChange={(e) => jumpToMonth(e.target.value)}
                  className="border-2 border-slate-200 rounded-lg p-2 text-slate-800 focus:outline-none focus:border-cor-base transition-colors"
                />
              </div>
            </>
          )}
        </div>

        <button
          onClick={() => changeMonth(1)}
          type="button"
          aria-label="Próximo mês"
          className="px-3 py-1.5 rounded-lg border-2 border-cor-base text-cor-base font-semibold hover:scale-[1.05] transition-all"
        >
          →
        </button>
      </div>

      {!loadingSummary && (
        <p className="text-sm text-slate-600 mt-3 text-center">
          {presentDaysCount === 0
            ? "Nenhuma presença registrada neste mês."
            : `${presentDaysCount} dia${presentDaysCount > 1 ? "s" : ""} com presença neste mês.`}
        </p>
      )}

      <div className="mt-4 grid grid-cols-7 gap-1 text-center">
        {WEEKDAYS.map((w, idx) => (
          <div key={idx} className="text-xs font-semibold text-slate-400 py-1">
            {w}
          </div>
        ))}
        {daysGrid.map((date, idx) => {
          if (!date) return <div key={idx} />
          const dateStr = toDateString(date)
          const wasPresent = Boolean(summary[dateStr])
          const isSelected = dateStr === selectedDate
          const isToday = dateStr === toDateString(today)

          return (
            <button
              key={idx}
              type="button"
              onClick={() => setSelectedDate(dateStr)}
              className={`relative aspect-square rounded-lg text-sm font-semibold flex items-center justify-center transition-all ${
                isSelected
                  ? "bg-cor-base text-slate-100"
                  : isToday
                  ? "border-2 border-cor-base text-cor-base"
                  : "text-slate-700 hover:bg-slate-200"
              }`}
            >
              {date.getDate()}
              {wasPresent && (
                <span
                  className={`absolute bottom-1 w-1.5 h-1.5 rounded-full ${
                    isSelected ? "bg-slate-100" : "bg-cor-base"
                  }`}
                  title="Presença registrada"
                />
              )}
            </button>
          )
        })}
      </div>
      {loadingSummary && (
        <p className="text-xs text-slate-400 mt-2">Carregando calendário...</p>
      )}

      <div className="mt-6">
        <h3 className="font-semibold text-slate-900 capitalize">
          {selectedDateLabel}
        </h3>

        <div className="mt-3">
          {loadingDetail ? (
            <p className="text-slate-500">Carregando...</p>
          ) : dayDetail?.present ? (
            <div className="flex items-center gap-3 bg-white border-2 border-cor-base/30 rounded-xl p-4">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-cor-base text-slate-100 font-bold">
                ✓
              </span>
              <div>
                <p className="font-semibold text-slate-900">Presença registrada</p>
                <p className="text-sm text-slate-600">
                  Horário: {dayDetail.horario?.slice(0, 5)}
                </p>
              </div>
            </div>
          ) : (
            <p className="text-slate-500 text-center py-6 bg-white border-2 border-slate-100 rounded-xl">
              Você não registrou presença neste dia.
            </p>
          )}
        </div>
      </div>
    </div>
  )
}
